/**
 * generateFinancialReport
 *
 * Builds a print-ready HTML document and opens it in a new tab.
 * Waits for all invoice images to load (up to 10 s) before calling
 * window.print() so they appear in the PDF.
 *
 * @param {object} camp            — The selected camp object ({ name, start_date, end_date, … })
 * @param {object} incomeBreakdown — { methods: [{method, count, total}], grossIncome, totalRefunded, netIncome }
 * @param {object} expenseSummary  — { general: [{description, category, amount, created_at, invoice_url}], totalGeneral, netResult }
 * @param {object} donationsSummary — { list: [{donor_name, description, category, amount, donated_at, receipt_url}], total }
 */
import i18n from '../i18n'
import { paymentMethodLabel } from './paymentMethods'

export async function generateFinancialReport(camp, incomeBreakdown, expenseSummary, donationsSummary, projectedSummary) {
  // Escape DB-sourced values before interpolating into the print HTML
  const esc = s => String(s ?? '')
    .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;').replaceAll("'", '&#39;')

  const fmt = n =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0)

  const fmtDate = dateStr => {
    if (!dateStr) return '—'
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    })
  }

  const fmtTs = ts => {
    if (!ts) return '—'
    return new Date(ts).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    })
  }

  const campName      = camp?.name ?? 'Camp'
  const generatedDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  })

  const dateRange =
    camp?.start_date && camp?.end_date
      ? `${fmtDate(camp.start_date)} – ${fmtDate(camp.end_date)}`
      : camp?.start_date
        ? fmtDate(camp.start_date)
        : ''

  // ── Income rows ─────────────────────────────────────────────────────────
  const incomeRows = incomeBreakdown.methods.map(m => `
    <tr>
      <td>${esc(paymentMethodLabel(m.method, i18n.t.bind(i18n)) ?? m.method)}</td>
      <td class="num">${fmt(m.total)}</td>
    </tr>`).join('')

  // ── General expenses table rows ─────────────────────────────────────────
  const expenseTableRows = expenseSummary.general.length > 0
    ? expenseSummary.general.map((e, i) => `
    <tr style="background:${i % 2 === 0 ? '#ffffff' : '#f9fafb'};">
      <td>${esc(e.description)}</td>
      <td>${esc(e.category ?? 'Other')}</td>
      <td class="num">${fmt(e.amount)}</td>
    </tr>`).join('')
    : `<tr><td colspan="3" class="empty">${i18n.t('admin.reportNoExpenses')}</td></tr>`

  // ── Expense detail cards ─────────────────────────────────────────────────
  const netResultColor = expenseSummary.netResult >= 0 ? '#15803d' : '#dc2626'
  const netResultSign  = expenseSummary.netResult >= 0 ? '+' : ''

  const expenseCards = expenseSummary.general.length > 0
    ? expenseSummary.general.map(e => {
        const isPdf    = /\.pdf(\?|$)/i.test(e.invoice_url ?? '')
        const isImage  = e.invoice_url && !isPdf

        const invoiceBlock = isImage
          ? `<div class="expense-image">
              <img src="${esc(e.invoice_url)}" alt="Invoice — ${esc(e.description)}"
                   style="max-width:100%;max-height:400px;object-fit:contain;border-radius:8px;display:block;margin:0 auto;" />
              <p class="image-caption">Invoice: ${esc(e.description)}</p>
            </div>`
          : isPdf
            ? `<div class="expense-no-invoice" style="border-style:solid;border-color:#c3e0ed;background:#eff1eb;">
                <span style="font-size:24px;display:block;margin-bottom:6px;">📄</span>
                <a href="${esc(e.invoice_url)}" style="color:#64795c;font-size:13px;font-weight:600;">Open PDF Invoice →</a>
              </div>`
            : `<div class="expense-no-invoice">
                <span style="font-size:20px;display:block;margin-bottom:4px;">🧾</span>
                ${i18n.t('admin.reportNoInvoice')}
              </div>`

        return `
        <div class="expense-card">
          <div class="expense-header">
            <div class="expense-info">
              <p class="expense-desc">${esc(e.description)}</p>
              <span class="category-badge">${esc(e.category ?? 'Other')}</span>
              <p class="expense-date">Added ${fmtTs(e.created_at)}</p>
            </div>
            <div class="expense-amount">${fmt(e.amount)}</div>
          </div>
          <hr class="expense-divider" />
          ${invoiceBlock}
        </div>`
      }).join('')
    : `<p style="color:#9ca3af;font-style:italic;padding:16px 0;">${i18n.t('admin.reportNoExpenses')}</p>`

  // ── Donations table rows ────────────────────────────────────────────────
  const donationList = donationsSummary?.list ?? []
  const donationTotal = donationsSummary?.total ?? 0

  const donorName = d => {
    const n = (d.donor_name ?? '').trim()
    return n ? esc(n) : i18n.t('admin.donationAnonymous')
  }

  const donationTableRows = donationList.length > 0
    ? donationList.map((d, i) => `
    <tr style="background:${i % 2 === 0 ? '#ffffff' : '#f9fafb'};">
      <td>${donorName(d)}</td>
      <td>${esc(d.category ?? 'Other')}</td>
      <td class="num">${fmt(d.amount)}</td>
    </tr>`).join('')
    : `<tr><td colspan="3" class="empty">${i18n.t('admin.reportNoDonations')}</td></tr>`

  const donationCards = donationList.length > 0
    ? donationList.map(d => {
        const isPdf   = /\.pdf(\?|$)/i.test(d.receipt_url ?? '')
        const isImage = d.receipt_url && !isPdf

        const receiptBlock = isImage
          ? `<div class="expense-image">
              <img src="${esc(d.receipt_url)}" alt="Receipt — ${donorName(d)}"
                   style="max-width:100%;max-height:400px;object-fit:contain;border-radius:8px;display:block;margin:0 auto;" />
              <p class="image-caption">Receipt: ${donorName(d)}</p>
            </div>`
          : isPdf
            ? `<div class="expense-no-invoice" style="border-style:solid;border-color:#c3e0ed;background:#eff1eb;">
                <span style="font-size:24px;display:block;margin-bottom:6px;">📄</span>
                <a href="${esc(d.receipt_url)}" style="color:#64795c;font-size:13px;font-weight:600;">Open PDF Receipt →</a>
              </div>`
            : `<div class="expense-no-invoice">
                <span style="font-size:20px;display:block;margin-bottom:4px;">🧾</span>
                ${i18n.t('admin.reportNoInvoice')}
              </div>`

        return `
        <div class="expense-card">
          <div class="expense-header">
            <div class="expense-info">
              <p class="expense-desc">${donorName(d)}</p>
              <span class="category-badge">${esc(d.category ?? 'Other')}</span>
              ${d.description ? `<p class="expense-date">${esc(d.description)}</p>` : ''}
              <p class="expense-date">${fmtDate(d.donated_at)}</p>
            </div>
            <div class="expense-amount">${fmt(d.amount)}</div>
          </div>
          <hr class="expense-divider" />
          ${receiptBlock}
        </div>`
      }).join('')
    : `<p style="color:#9ca3af;font-style:italic;padding:16px 0;">${i18n.t('admin.reportNoDonations')}</p>`

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Financial Report — ${esc(campName)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 13px;
      color: #111827;
      background: #fff;
      padding: 40px 48px;
    }
    /* ── Header ── */
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 32px;
      padding-bottom: 20px;
      border-bottom: 2px solid #e5e7eb;
    }
    .header-left h1 { font-size: 1.375rem; font-weight: 700; color: #111827; }
    .header-left p  { font-size: 0.8125rem; color: #6b7280; margin-top: 4px; }
    .header-right   { font-size: 0.75rem; color: #9ca3af; text-align: right; }
    .header-right strong { display: block; color: #374151; margin-bottom: 2px; }
    /* ── Sections ── */
    .section { margin-bottom: 32px; }
    .section-title {
      font-size: 0.6875rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: #6b7280;
      margin-bottom: 10px;
      padding-bottom: 6px;
      border-bottom: 1px solid #e5e7eb;
    }
    /* ── Tables ── */
    table { width: 100%; border-collapse: collapse; }
    th {
      text-align: left;
      font-size: 0.6875rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #9ca3af;
      padding: 6px 0;
      border-bottom: 1px solid #f3f4f6;
    }
    td {
      padding: 7px 0;
      border-bottom: 1px solid #f9fafb;
      color: #374151;
      vertical-align: middle;
    }
    td.num { text-align: right; font-variant-numeric: tabular-nums; }
    th.num { text-align: right; }
    td.empty { color: #9ca3af; font-style: italic; padding: 12px 0; }
    .subtotal td {
      font-weight: 700;
      color: #111827;
      border-top: 1px solid #e5e7eb;
      border-bottom: none;
      padding-top: 10px;
    }
    /* ── Totals block (income) ── */
    .totals { margin-top: 12px; padding-top: 12px; border-top: 2px solid #e5e7eb; }
    .total-row {
      display: flex;
      justify-content: space-between;
      padding: 4px 0;
      font-size: 13px;
      color: #374151;
    }
    .total-row.highlight {
      font-weight: 700;
      font-size: 14px;
      border-top: 1px solid #e5e7eb;
      margin-top: 6px;
      padding-top: 10px;
    }
    .total-row .lbl { color: #374151; }
    .total-row .val { font-variant-numeric: tabular-nums; }
    .refund-val { color: #dc2626; }
    .net-income-val { color: #15803d; font-weight: 700; }
    /* ── Summary box ── */
    .summary-box {
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 20px 24px;
      max-width: 360px;
    }
    .summary-row {
      display: flex;
      justify-content: space-between;
      padding: 5px 0;
      font-size: 13px;
    }
    .summary-row.divider {
      border-top: 1px solid #e5e7eb;
      margin-top: 8px;
      padding-top: 12px;
    }
    .summary-row .lbl { color: #374151; }
    .summary-row .val { font-weight: 600; font-variant-numeric: tabular-nums; }
    .net-result-val { font-size: 1.125rem; font-weight: 800; }
    /* ── Expense cards ── */
    .expense-card {
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 20px 24px;
      margin-bottom: 20px;
      background: #ffffff;
    }
    .expense-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 16px;
    }
    .expense-info { flex: 1; min-width: 0; }
    .expense-desc {
      font-size: 15px;
      font-weight: 700;
      color: #111827;
      margin-bottom: 6px;
    }
    .category-badge {
      display: inline-block;
      background: #f3f4f6;
      color: #374151;
      border: 1px solid #e5e7eb;
      border-radius: 999px;
      padding: 2px 10px;
      font-size: 11px;
      font-weight: 600;
      margin-bottom: 6px;
    }
    .expense-date {
      font-size: 12px;
      color: #9ca3af;
      margin-top: 4px;
    }
    .expense-amount {
      font-size: 18px;
      font-weight: 800;
      color: #111827;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }
    .expense-divider {
      border: none;
      border-top: 1px solid #f3f4f6;
      margin: 16px 0;
    }
    .expense-image {
      background: #f9fafb;
      border-radius: 8px;
      padding: 16px;
      text-align: center;
    }
    .image-caption {
      margin-top: 10px;
      font-size: 11px;
      color: #9ca3af;
      font-style: italic;
    }
    .expense-no-invoice {
      background: #f9fafb;
      border: 2px dashed #e5e7eb;
      border-radius: 8px;
      padding: 28px 16px;
      text-align: center;
      color: #9ca3af;
      font-size: 13px;
    }
    .expense-total {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 14px 24px;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      margin-top: 4px;
    }
    .expense-total .lbl { font-size: 13px; font-weight: 700; color: #374151; }
    .expense-total .val { font-size: 16px; font-weight: 800; color: #111827; font-variant-numeric: tabular-nums; }
    /* ── Print ── */
    @media print {
      body { padding: 20px 24px; }
      a { text-decoration: none; }
      .expense-card { page-break-inside: avoid; }
      .expense-image { page-break-inside: avoid; }
    }
  </style>
</head>
<body>

  <!-- Header -->
  <div class="header">
    <div class="header-left">
      <h1>${i18n.t('admin.reportFinancialTitle')}</h1>
      <p>${esc(campName)}${dateRange ? ` &nbsp;·&nbsp; ${dateRange}` : ''}</p>
    </div>
    <div class="header-right">
      <strong>${i18n.t('admin.reportGenerated')}</strong>
      ${generatedDate}
    </div>
  </div>

  <!-- Section A: Income Breakdown -->
  <div class="section">
    <div class="section-title">${i18n.t('admin.reportIncomeBreakdown')}</div>
    <table>
      <thead>
        <tr>
          <th>${i18n.t('admin.reportColPaymentMethod')}</th>
          <th class="num">${i18n.t('admin.reportColAmount')}</th>
        </tr>
      </thead>
      <tbody>
        ${incomeRows || `<tr><td colspan="2" class="empty">${i18n.t('admin.reportNoApprovedReceipts')}</td></tr>`}
      </tbody>
    </table>
    <div class="totals">
      <div class="total-row">
        <span class="lbl">${i18n.t('admin.reportGrossIncome')}</span>
        <span class="val">${fmt(incomeBreakdown.grossIncome)}</span>
      </div>
      <div class="total-row">
        <span class="lbl">${i18n.t('admin.reportRefunds')}</span>
        <span class="val refund-val">− ${fmt(incomeBreakdown.totalRefunded)}</span>
      </div>
      <div class="total-row highlight">
        <span class="lbl">${i18n.t('admin.reportNetIncome')}</span>
        <span class="val net-income-val">${fmt(incomeBreakdown.netIncome)}</span>
      </div>
    </div>
  </div>

  <!-- Section A2: Donations -->
  <div class="section">
    <div class="section-title">${i18n.t('admin.reportDonations')}</div>
    <table>
      <thead>
        <tr>
          <th>${i18n.t('admin.reportColDonor')}</th>
          <th>${i18n.t('admin.reportColCategory')}</th>
          <th class="num">${i18n.t('admin.reportColAmount')}</th>
        </tr>
      </thead>
      <tbody>
        ${donationTableRows}
      </tbody>
      ${donationList.length > 0 ? `
      <tfoot>
        <tr class="subtotal">
          <td colspan="2">${i18n.t('admin.reportTotalDonations')}</td>
          <td class="num">${fmt(donationTotal)}</td>
        </tr>
      </tfoot>` : ''}
    </table>
  </div>

  <!-- Section B: General Expenses -->
  <div class="section">
    <div class="section-title">${i18n.t('admin.reportGeneralExpenses')}</div>
    <table>
      <thead>
        <tr>
          <th>${i18n.t('admin.reportColDescription')}</th>
          <th>${i18n.t('admin.reportColCategory')}</th>
          <th class="num">${i18n.t('admin.reportColAmount')}</th>
        </tr>
      </thead>
      <tbody>
        ${expenseTableRows}
      </tbody>
      ${expenseSummary.general.length > 0 ? `
      <tfoot>
        <tr class="subtotal">
          <td colspan="2">${i18n.t('admin.reportTotalExpenses')}</td>
          <td class="num">${fmt(expenseSummary.totalGeneral)}</td>
        </tr>
      </tfoot>` : ''}
    </table>
  </div>

  <!-- Section C: Financial Summary -->
  <div class="section">
    <div class="section-title">${i18n.t('admin.reportFinancialSummary')}</div>
    <div class="summary-box">
      <div class="summary-row">
        <span class="lbl">${i18n.t('admin.reportNetIncomeLabel')}</span>
        <span class="val">${fmt(incomeBreakdown.netIncome)}</span>
      </div>
      <div class="summary-row">
        <span class="lbl">${i18n.t('admin.reportDonationsLabel')}</span>
        <span class="val">+ ${fmt(donationTotal)}</span>
      </div>
      <div class="summary-row">
        <span class="lbl">${i18n.t('admin.reportGeneralExpensesLabel')}</span>
        <span class="val">− ${fmt(expenseSummary.totalGeneral)}</span>
      </div>
      <div class="summary-row divider">
        <span class="lbl" style="font-weight:700;">${i18n.t('admin.reportNetResult')}</span>
        <span class="val net-result-val" style="color:${netResultColor};">
          ${netResultSign}${fmt(expenseSummary.netResult)}
        </span>
      </div>
    </div>
  </div>

  ${projectedSummary && projectedSummary.pendingAgreementIncome > 0 ? `
  <div class="section">
    <div class="section-title">${i18n.t('admin.summaryProjected')}</div>
    <div class="summary-box">
      <div class="summary-row">
        <span class="lbl">${i18n.t('admin.pendingAgreementIncome')}</span>
        <span class="val">+ ${fmt(projectedSummary.pendingAgreementIncome)}</span>
      </div>
      <div class="summary-row divider">
        <span class="lbl" style="font-weight:700;">${i18n.t('admin.projectedNetResult')}</span>
        <span class="val">${fmt(projectedSummary.projectedNetResult)}</span>
      </div>
    </div>
  </div>` : ''}

  <!-- Section D: Expense Detail -->
  <div class="section">
    <div class="section-title">${i18n.t('admin.reportExpenseDetail')}</div>
    ${expenseCards}
    ${expenseSummary.general.length > 0 ? `
    <div class="expense-total">
      <span class="lbl">${i18n.t('admin.reportTotalExpenses')}</span>
      <span class="val">${fmt(expenseSummary.totalGeneral)}</span>
    </div>` : ''}
  </div>

  <!-- Section E: Donation Detail -->
  <div class="section">
    <div class="section-title">${i18n.t('admin.reportDonations')}</div>
    ${donationCards}
    ${donationList.length > 0 ? `
    <div class="expense-total">
      <span class="lbl">${i18n.t('admin.reportTotalDonations')}</span>
      <span class="val">${fmt(donationTotal)}</span>
    </div>` : ''}
  </div>

</body>
</html>`

  const win = window.open('', '_blank')
  if (!win) {
    // eslint-disable-next-line no-alert
    alert(i18n.t('admin.reportPopupBlocked'))
    return
  }
  win.document.write(html)
  win.document.close()

  // Wait for all invoice images to load before printing (max 10 s)
  const images = Array.from(win.document.querySelectorAll('img'))
  const loadPromises = images.map(img => new Promise(resolve => {
    if (img.complete) { resolve(); return }
    img.onload  = resolve
    img.onerror = resolve // don't hang on broken images
  }))
  const timeout = new Promise(resolve => setTimeout(resolve, 10_000))
  await Promise.race([Promise.all(loadPromises), timeout])

  win.print()
}
